import { TarotCard } from './TarotCard';
import type { DrawnCard, TarotSpread } from '../../types/tarot';
import '../../styles/tarot-animations.css';

interface CelticCrossLayoutProps {
  cards: DrawnCard[];
  spread: TarotSpread;
  revealedCards: Set<number>;
  onCardClick?: (index: number) => void;
  showMeanings?: boolean;
  // Decoration customization props
  equippedDeckId?: string | null;
  equippedCardBackPath?: string | null;
}

export function CelticCrossLayout({
  cards,
  spread,
  revealedCards,
  onCardClick,
  showMeanings = false,
  equippedDeckId = null,
  equippedCardBackPath = null
}: CelticCrossLayoutProps) {
  if (spread.id !== 'celtic-cross' || cards.length !== 10) {
    return null;
  }

  // Celtic Cross specific positioning with FIXED PIXEL positions
  // Card size: 110px × 193px (sm size, 1:1.75 ratio)
  // Container: 1000px × 800px with 60px padding
  // Using fixed pixel positions with generous spacing to prevent overlap
  const celticCrossPositions = [
    // Cross formation (cards 1-6) - Center at (450, 400)
    { id: 1, x: 450, y: 400, name: 'Present', rotation: 0 }, // Center
    { id: 2, x: 450, y: 400, name: 'Challenge', rotation: 90 }, // Crossing (horizontal, same position)
    { id: 3, x: 250, y: 400, name: 'Past', rotation: 0 }, // Left (200px left of center)
    { id: 4, x: 450, y: 600, name: 'Foundation', rotation: 0 }, // Bottom (200px below center)
    { id: 5, x: 450, y: 200, name: 'Crown', rotation: 0 }, // Top (200px above center)
    { id: 6, x: 650, y: 400, name: 'Future', rotation: 0 }, // Right (200px right of center)
    
    // Staff formation (cards 7-10) - vertical line on the right at x=850
    { id: 7, x: 850, y: 600, name: 'Self', rotation: 0 }, // Bottom of staff
    { id: 8, x: 850, y: 467, name: 'Environment', rotation: 0 }, // Third from bottom (133px spacing)
    { id: 9, x: 850, y: 334, name: 'Hopes/Fears', rotation: 0 }, // Second from bottom (133px spacing)
    { id: 10, x: 850, y: 200, name: 'Outcome', rotation: 0 } // Top of staff (134px spacing)
  ];

  return (
    <div className="relative w-full overflow-x-auto celtic-cross-animation">
      {/* Fixed size container to hold the layout - increased size with more padding */}
      <div className="relative mx-auto" style={{ width: '1000px', height: '800px', minWidth: '1000px', padding: '60px' }}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg viewBox="0 0 1000 800" className="w-full h-full">
            {/* Celtic cross background pattern */}
            <circle cx="450" cy="400" r="140" fill="none" stroke="currentColor" strokeWidth="1" />
            <line x1="250" y1="400" x2="650" y2="400" stroke="currentColor" strokeWidth="1" />
            <line x1="450" y1="200" x2="450" y2="600" stroke="currentColor" strokeWidth="1" />
          </svg>
        </div>

        {/* Cards */}
        {cards.map((card, index) => {
          const position = celticCrossPositions[index];
          const isCardRevealed = revealedCards.has(index);

          return (
            <div
              key={`${card.id}-${index}`}
              className="absolute"
              style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                transform: `translate(-50%, -50%) rotate(${position.rotation}deg)`,
                zIndex: index === 1 ? 10 : 5 // Challenge card (crossing) on top
              }}
            >
              <div className="text-center">
                <TarotCard
                  card={card}
                  isRevealed={isCardRevealed}
                  size="sm"
                  showMeaning={false}
                  enableFlipAnimation={false} // DISABLED
                  enableHoverEffects={true}
                  animationDelay={0} // DISABLED
                  position={index + 1}
                  spreadType="celtic-cross"
                  equippedDeckId={equippedDeckId}
                  equippedCardBackPath={equippedCardBackPath}
                  onClick={() => onCardClick?.(index)}
                />
                
                {/* Position label */}
                <div className="mt-1">
                  <div className="text-xs font-semibold text-primary-400">
                    {position.name}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Connection lines (subtle visual guides) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20">
          {/* Cross formation lines */}
          <line 
            x1="250" y1="400" 
            x2="650" y2="400" 
            stroke="currentColor" 
            strokeWidth="1" 
            strokeDasharray="4,4"
          />
          <line 
            x1="450" y1="200" 
            x2="450" y2="600" 
            stroke="currentColor" 
            strokeWidth="1" 
            strokeDasharray="4,4"
          />
          
          {/* Staff line */}
          <line 
            x1="850" y1="200" 
            x2="850" y2="600" 
            stroke="currentColor" 
            strokeWidth="1" 
            strokeDasharray="4,4"
          />
        </svg>

        {/* Legend */}
        <div className="absolute bottom-2 left-2">
          <div className="bg-dark-800/90 rounded p-2 text-xs text-gray-400">
            <div className="font-semibold text-white mb-1">Celtic Cross</div>
            <div>Cross: Past → Present → Future</div>
            <div>Staff: Inner → Outer influences</div>
          </div>
        </div>
      </div>
    </div>
  );
}